/**
 *  Version page
 *  Author: Teemo
 *  CreatedAt: 2021-03-04
 */
import { NoDataPage } from '@/src/components';
import Breadcrumb from '@/src/components/Antd/Breadcrumb/Breadcrumb';
import ModalFooter from '@/src/components/Antd/Modal/ModalFooter';
import Table from '@/src/components/EditableTable/EditableTableTeemo';
import ErrorModal from '@/src/components/ErrorModal/ErrorModal';
import { EnumQuery } from '@/src/pages/SalesChannelContentEditor/common';
import { SalesChannelContent } from '@/src/request/salesChannel/types';
import {
  createVersion,
  defaultVersion,
  deleteSalesChannelVersion,
  duplicateVersion,
  getSalesChannelContents,
  unPublishVersion,
  versionRename,
} from '@/src/request/salesChannel/versionContent';
import { nameCopy } from '@/src/tools/helper';
import { SalesChannelContentStatus } from '@/src/types/common/common';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { message } from 'antd';
import { get } from 'lodash';
import React, {
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Modal } from 'src/components/Antd';
import Query from 'src/constrains/Query/Query';
import { useI18n } from 'src/hooks/useRouterChange';
import ListPageLayout from 'src/layout/ListPageLayout/ListPageLayout';
import { i18nGet } from 'src/tools/tools';
import {
  breadcrumbData,
  columnsDraft,
  columnsNoDraft,
} from './SalesChannelVersionContentConfig';
import AddVersionModal from './components/AddVersionModal/AddVersionModal';
import PublishModal from './components/PublishModal/publishModal';
import css from './css.less';
import { deleteSearch, searchList } from './store';
//import { nameDict, EnumFieldType } from 'src/types/common/customerDb';
interface RenameData {
  uuid: string;
  newName: string;
}
interface IParams {
  uuid: string;
}

import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from 'src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from 'src/types/common/resources';

const permissions = (saleChannelUuid) => {
  const canDelete = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        uuid: saleChannelUuid,
        category: EnumCategory.SalesChannelContent,
        subCategory: EnumSubCategory.ChannelContent,
        operation: EnumOperation.delete_version,
      }),
    ],
  });
  const canView = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        uuid: saleChannelUuid,
        category: EnumCategory.SalesChannelContent,
        subCategory: EnumSubCategory.ChannelContent,
        operation: EnumOperation.view_content_version_details,
      }),
    ],
  });
  const canCreateDraft = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        uuid: saleChannelUuid,
        category: EnumCategory.SalesChannelContent,
        subCategory: EnumSubCategory.ChannelContent,
        operation: EnumOperation.create_duplicate_edit_draft_content_version,
      }),
    ],
  });
  const canCreateLive = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        uuid: saleChannelUuid,
        category: EnumCategory.SalesChannelContent,
        subCategory: EnumSubCategory.ChannelContent,
        operation: EnumOperation.create_duplicate_edit_live_content_version,
      }),
    ],
  });
  const canPublish = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        uuid: saleChannelUuid,
        category: EnumCategory.SalesChannelContent,
        subCategory: EnumSubCategory.ChannelContent,
        operation: EnumOperation.publish_unpublish_version,
      }),
    ],
  });
  return { canDelete, canView, canCreateDraft, canCreateLive, canPublish };
};

const SalesChannelVersionContent = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const state = useSelector((state: any) => state.SalesChannelVersionContent);
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const { uuid: saleChannelUuid = '' } = params as IParams;
  const { canDelete, canView, canCreateDraft, canCreateLive, canPublish } =
    permissions(saleChannelUuid);
  const { searchList: searchListArray } = state;

  //loading
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState<string | ReactNode>('');
  const [modalContent, setModalContent] = useState<string | ReactNode>('');
  const [modalWidth, setModalWidth] = useState<number>(600);
  const [draftsData, setDraftsData] = useState<SalesChannelContent[]>([]);
  const [noDraftsData, setNoDraftsData] = useState<SalesChannelContent[]>([]);
  const [rename, setRename] = useState<RenameData>();
  const [cannelName, setCannelName] = useState<string>('');
  const [timezoneGmt, setTimezoneGmt] = useState<string>('');

  const nonDefaultActiveTimePeriod = React.useMemo(() => {
    const nonDefaultActive = noDraftsData.find(
      (i) => i.status === SalesChannelContentStatus.Live && !i.defaultVersion,
    );

    const beginDate = get(nonDefaultActive, 'beginDate');
    const beginTime = get(nonDefaultActive, 'beginTime');
    const endDate = get(nonDefaultActive, 'endDate');
    const endTime = get(nonDefaultActive, 'endTime');
    const t = [];
    //console.log('t', t);
    if (beginDate && beginTime) {
      t.push(`${beginDate} ${beginTime}`);
    }
    if (endDate && endTime) {
      t.push(`${endDate} ${endTime}`);
    }

    return t;
  }, [noDraftsData]);

  const draftsNameList = draftsData.map((item) => item.versionName);
  const noDraftsDataNameList = noDraftsData.map((item) => item.versionName);
  const nameList = noDraftsDataNameList.concat(draftsNameList);

  useEffect(() => {
    getVersionList();
  }, []);

  const draftsSearchData = useMemo(
    () =>
      searchListArray.length > 0
        ? draftsData.filter((item) => {
            return searchListArray.every((search) => {
              return (
                item.versionName
                  .toLocaleLowerCase()
                  .indexOf(search.toLocaleLowerCase()) !== -1
              );
            });
          })
        : draftsData,

    [searchListArray, draftsData],
  );

  const noDraftsSearchData = useMemo(
    () =>
      searchListArray.length > 0
        ? noDraftsData.filter((item) => {
            return searchListArray.every((search) => {
              return (
                item.versionName
                  .toLocaleLowerCase()
                  .indexOf(search.toLocaleLowerCase()) !== -1
              );
            });
          })
        : noDraftsData,
    [searchListArray, noDraftsData],
  );

  /**
   * Request Version list
   *
   */
  const getVersionList = () => {
    if (!loading) {
      setLoading(true);
    }
    getSalesChannelContents(saleChannelUuid)
      .then((res) => {
        const {
          drafts = [],
          nonDrafts = [],
          salesChannelName = '',
          timezoneGmt = '',
        } = res;
        setDraftsData(drafts);
        setNoDraftsData(nonDrafts);
        setCannelName(salesChannelName);
        setTimezoneGmt(timezoneGmt);
        setLoading(false);
      })
      .catch((error) => {
        console.error(`getVersionList error ${error}`);
      });
  };

  const checkDefaultVersion = async (uuid) => {
    const res = await defaultVersion(uuid);
    return res;
  };

  const duplicateName = (name) => {
    return nameCopy(name, nameList).substring(0, 100);
  };

  /**
   * Added a new Version
   *
   * @param {*} name
   */
  const handleAddOnSubmit = (name) => {
    const data = { versionName: name, salesChannelUuid: saleChannelUuid };
    createVersion(data)
      .then((res) => {
        message.success('success');
        history.push(
          `/salesChannel/editor/content/homepage/${saleChannelUuid}/${res.uuid}?${EnumQuery.isNew}=1`,
        );
      })
      .catch((error) => {
        console.error(`createVersion error ${error}`);
      });
  };

  /**
   * Delete a Version
   *
   * @param {*} uuid
   */
  const handleDeleteVersion = (uuid) => {
    deleteSalesChannelVersion(uuid)
      .then((res) => {
        message.success('success');
        getVersionList();
      })
      .catch((error) => {
        console.error(`handleDeleteVersion error ${error}`);
      });
  };
  /**
   * unPublish one Version
   *
   * @param {*} uuid
   */
  const unPublishOneVersion = (uuid) => {
    unPublishVersion(uuid)
      .then((res) => {
        message.success('success');
        getVersionList();
      })
      .catch((error) => {
        console.error(`unPublishOneVersion error ${error}`);
      });
  };
  /**
   * Duplicate Version Request
   *
   * @param {*} uuid
   */
  const handleDuplicate = (name, duplicateUuid) => {
    const data = { contentName: name, uuid: duplicateUuid };
    duplicateVersion(data)
      .then((res) => {
        message.success(getI18n('general.duplicatesuccessfully'));
        getVersionList();
      })
      .catch((error) => {
        console.error(`handleDuplicate error ${error}`);
      });
  };

  /**
   * duplicateModal
   *
   * @param {*} defaultName
   * @param {*} duplicateUuid
   */
  const handleDuplicateModal = (defaultName, duplicateUuid) => {
    setModalWidth(600);
    setShowModal(true);
    setModalTitle(getI18n('salesChannelContent.index.duplicateversion'));
    const child = (
      <AddVersionModal
        defaultValue={duplicateName(defaultName)}
        title={getI18n('salesChannelContent.index.newVersionName')}
        okText={getI18n('general.duplicate')}
        setShowModal={setShowModal}
        getVersionList={getVersionList}
        onSubmit={(name) => handleDuplicate(name, duplicateUuid)}
        //nonDefaultActiveTimePeriod={nonDefaultActiveTimePeriod}
      />
    );
    setModalContent(child);
  };

  /**
   * The new Popup window
   *
   */
  const handleAddFun = () => {
    setModalWidth(600);
    setShowModal(true);
    setModalTitle(getI18n('salesChannelContent.index.createnewversion'));
    const versionName = `Version ${nameList.length + 1}`;
    const child = (
      <AddVersionModal
        defaultValue={versionName}
        title={getI18n('salesChannelContent.index.versionname')}
        okText={getI18n('general.create')}
        setShowModal={setShowModal}
        onSubmit={handleAddOnSubmit}
      />
    );
    setModalContent(child);
  };

  /**
   * delete Modal & UnPublish Modal
   *
   * @param {*} uuid
   */
  const handleMultiErrorModal = async (
    uuid,
    type: SalesChannelContentStatus,
    fun: 'Delete' | 'UnPublish',
    isDefault,
    { getI18n },
  ) => {
    setModalWidth(600);

    const check = await checkDefaultVersion(uuid);
    let errorContent = '';
    switch (type) {
      case SalesChannelContentStatus.Draft:
        errorContent = getI18n('salesChannelContent.index.multiErrorText1');
        break;
      case SalesChannelContentStatus.Scheduled:
        if (fun === 'Delete') {
          errorContent = getI18n('salesChannelContent.index.multiErrorText2');
        } else {
          errorContent = getI18n('salesChannelContent.index.multiErrorText3');
        }
        break;
      case SalesChannelContentStatus.Live:
        if (fun === 'Delete') {
          if (isDefault || !check.defaultVersion || check.scheduled) {
            errorContent = getI18n('salesChannelContent.index.multiErrorText4');
          } else {
            errorContent = getI18n('salesChannelContent.index.multiErrorText2');
          }
        } else {
          if (isDefault || !check.defaultVersion || check.scheduled) {
            errorContent = getI18n('salesChannelContent.index.multiErrorText5');
          } else {
            errorContent = getI18n('salesChannelContent.index.multiErrorText6');
          }
        }
        break;
    }
    setModalContent(
      <ErrorModal
        setModal={setShowModal}
        onSubmit={() =>
          fun === 'UnPublish'
            ? unPublishOneVersion(uuid)
            : handleDeleteVersion(uuid)
        }
        okText={
          fun === 'UnPublish'
            ? getI18n('salesChannelContent.index.unPublish')
            : getI18n('general.antd.Modal.ModalFooter.delete')
        }
        errorText={errorContent}
      />,
    );
    setModalTitle(
      <div>
        <ExclamationCircleFilled className={css.errModalIcon} />{' '}
        {fun === 'UnPublish'
          ? getI18n('salesChannelContent.index.unPublish')
          : getI18n('salesChannelContent.index.deleteversion')}
      </div>,
    );
    setShowModal(true);
  };

  /**
   * Publish or Schedule Modal
   *
   * @param {string} uuid
   * @param type
   */
  const handlePublish = ({ uuid, type, err, status }: any) => {
    if (err) {
      notAblePublish(uuid);
      return false;
    }
    setModalWidth(600);
    setShowModal(true);
    setModalTitle(type);
    setModalContent(
      <PublishModal
        getVersionList={getVersionList}
        uuid={uuid}
        disabled={
          status === SalesChannelContentStatus.Live ? [true, false] : false
        }
        setModal={setShowModal}
        nonDefaultActiveTimePeriod={nonDefaultActiveTimePeriod}
      />,
    );
  };
  /**Not allowed to Publish */
  const notAblePublish = (uuid) => {
    const notAblePublishText = (
      <div>
        <div className={css.errorPublish}>
          {getI18n('salesChannelContent.index.errorPublish')}
        </div>{' '}
        <ModalFooter
          onCancel={() => setShowModal(false)}
          okText={getI18n('general.gotoedit')}
          onOk={() => handleEditContent(uuid, EnumQuery.hasErrorCollapse)}
        />
      </div>
    );
    setModalContent(notAblePublishText);
    setModalTitle(
      <div>
        <ExclamationCircleFilled className={css.errModalIcon} />{' '}
        {getI18n('salesChannelContent.index.notabletopublish')}
      </div>,
    );
    setShowModal(true);
  };

  // Click on the search button | | press enter
  const handleSearch = (value) => {
    dispatch(searchList(value));
  };

  //Closing tags
  const handleTagClose = (index) => {
    dispatch(deleteSearch(index));
  };

  //条件查询
  const queryProps = {
    searchList: searchListArray,
    deleteSearch,

    handleSearch,
    onSearch: handleSearch,
    onPress: handleSearch,
    onTagClose: handleTagClose,
  };

  const handleModalCancel = () => {
    setShowModal(false);
  };
  /**
   *Input box onChange
   *
   * @param {*} isDelete
   * @param {*} newName
   * @param {*} uuid
   */
  const handleVersionOnChange = (isDelete, newName, uuid) => {
    console.log(isDelete, { newName, uuid });
    setRename({ newName: newName.name, uuid });
  };

  /** Input field loses focus  */
  const handleTableOnBlur = async () => {
    if (rename) {
      if (
        draftsNameList.includes(rename.newName) ||
        noDraftsDataNameList.includes(rename.newName)
      ) {
        message.error(getI18n('salesChannelContent.index.duplicatename'));
      } else {
        await versionRename(rename).then(() => {
          getVersionList();
        });
      }
    }
  };

  //The Header configuration
  const pageLayoutProps = {
    title: getI18n('general.sider.content'),
    addText: getI18n('salesChannelContent.index.createNewVersion'),
    addFun: canCreateDraft ? handleAddFun : null,
  };
  const handleEditContent = (uuid, type) => {
    history.push(
      `/salesChannel/editor/content/homepage/${saleChannelUuid}/${uuid}?${
        EnumQuery.hasErrorCollapse
      }${type === EnumQuery.hasErrorCollapse ? '=1' : '=0'}`,
    );
  };

  return (
    <React.Fragment>
      {/* {nameDict[EnumFieldType.FirstName]} */}
      <Breadcrumb
        className={css.breadcrumb}
        breadcrumbData={breadcrumbData(cannelName, { getI18n })}></Breadcrumb>
      {!loading && draftsData.length === 0 && noDraftsData.length === 0 && (
        <div className={css.placeholder}>
          <NoDataPage
            title={getI18n('salesChannelContent.index.noDataPageTitle')}
            introduce={
              <Fragment>
                <p className={css.introduce}>
                  {getI18n('salesChannelContent.index.noDataPageIntroduce1')}
                </p>
                <p className={css.introduce}>
                  {getI18n('salesChannelContent.index.noDataPageIntroduce2')}
                </p>
              </Fragment>
            }
            buttonTitle={getI18n('salesChannelContent.index.createNewVersion')}
            onClick={handleAddFun}
          />
        </div>
      )}
      {(noDraftsData.length > 0 || draftsData.length > 0) && (
        <React.Fragment>
          <ListPageLayout {...pageLayoutProps}>
            <Query {...queryProps} />
            {noDraftsData.length > 0 && (
              <Table
                id='NoDraftTable'
                collapsible={true}
                className={css.noDraftTable}
                collapsibleText={`${noDraftsSearchData.length} ${getI18n(
                  'salesChannelContent.index.activeversion',
                )}`}
                dataSource={noDraftsSearchData}
                columns={columnsNoDraft(
                  (isDelete, index, settings) =>
                    handleVersionOnChange(isDelete, index, settings),
                  handleTableOnBlur,
                  (name, uuid) => handleDuplicateModal(name, uuid),
                  (uuid, type, fun, isDefault) =>
                    handleMultiErrorModal(uuid, type, fun, isDefault, {
                      getI18n,
                    }),
                  (uuid, type, status) => handlePublish({ uuid, type, status }),
                  (uuid) => handleEditContent(uuid, ''),
                  saleChannelUuid,
                  timezoneGmt,
                  canView,
                  canCreateLive,
                  canPublish,
                  canDelete,
                  { getI18n },
                )}
                rowKey='uuid'
                hidePagination={true}
                loading={loading}
              />
            )}
            {draftsData.length > 0 && (
              <Table
                id='DraftTable'
                collapsible={true}
                className={css.draftsTale}
                data={draftsSearchData}
                collapsibleText={`${draftsSearchData.length} ${getI18n(
                  'salesChannelContent.index.draftversion',
                )}`}
                columns={columnsDraft(
                  (isDelete, index, settings) =>
                    handleVersionOnChange(isDelete, index, settings),
                  handleTableOnBlur,
                  (name, uuid) => handleDuplicateModal(name, uuid),
                  (uuid, type, fun, isDefault) =>
                    handleMultiErrorModal(uuid, type, fun, isDefault, {
                      getI18n,
                    }),
                  (uuid, type, err) => handlePublish({ uuid, type, err }),
                  (uuid) => handleEditContent(uuid, ''),
                  saleChannelUuid,
                  timezoneGmt,
                  canView,
                  canCreateDraft,
                  canPublish,
                  canDelete,
                  { getI18n },
                )}
                rowKey='uuid'
                hidePagination={true}
                loading={loading}
              />
            )}
          </ListPageLayout>
        </React.Fragment>
      )}
      <Modal
        width={modalWidth || 600}
        visible={showModal}
        title={modalTitle}
        onCancel={handleModalCancel}
        maskClosable={false}
        cancelText={getI18n('general.cancel')}>
        {modalContent}
      </Modal>
    </React.Fragment>
  );
};
export default SalesChannelVersionContent;
